// THE FAR ATLAS (rearch-r5d-far-tree-client, APP_GROUNDM.md "The far field"):
// the far ring's sector pixels in ONE texture, which the far tree's nodes
// (groundtree.js, terrain.js V_FAR) sample through look.at's rule.
//
// One 2D texture, RGBA8 sRGB, FAR_LEVELS mip levels (texStorage2D, no data),
// sized per device by renderconfig vt.far (farAtlasPx: by_class only lowers;
// clamped to MAX_TEXTURE_SIZE). Created once by TerrainManager when the bake
// has a far tree (meta.groundm.outer; terrain.farAtlas), its storage
// allocated at the first alloc (the renderer exists by then).
//
// The painter (ringgen.js, the 'ring' gen pass):
//   alloc(id, px, level, quick)  -> entry | null (no room: a painted alloc
//            first evicts QUICK entries to flat, a quick alloc never does).
//            entry = { id, level, x, y,
//            w, h (the box incl. the pad, atlas texels from the GL bottom),
//            inner [x0, y0, x1, y1], px [iw, ih] = max(1, px >> level) }.
//            A box is FAR_CELL-aligned, a multiple of FAR_CELL, with FAR_PAD
//            texels around the inner rect: level FAR_LEVELS - 1 keeps one
//            clean texel of pad. A second alloc for a resident id is its
//            replacement: the old entry draws until commit.
//   target(entry) -> { framebuffer (level 0), viewport = inner }: the painter
//            fills the INNER rect of level 0 only, as into its own texture
//            (v 1 = north = the viewport's top; GL rows from the south).
//   upload(entry, rgba) the inner rect of level 0 from CPU bytes (sRGB8,
//            row 0 = south: ringref ringPaint's rgba) — the quick paint's
//   commit(entry, rgb?) the pad edge-replicated (blits through two strip textures:
//            WebGL forbids a blit inside one image), levels 1.. box-filtered
//            for that box only (LINEAR 2:1 blits, sRGB-aware; never a whole
//            texture generateMipmap), the old entry freed, the table row set,
//            version++, onChange([id]). rgb (sRGB8, the entry's mean): a
//            QUICK entry (ringgen.js, provisional) — a painted alloc short of
//            room turns it flat in rgb (fallback's state 2: no texels), so a
//            sector once coloured never goes back to the no-slot grey.
//   fallback(id, rgb) the sector drawn flat (rgb: sRGB bytes, ring.fallback's).
//   free(id) an abandoned alloc / a dropped sector.
//   lookup(id) -> { entry, quick } | { rgb } | null.
//   budget = { w, h, levels, pad, bytes, fits(list), fitsSteps(list) }: fits([{ px, level }])
//            the dry-run pack (largest first) planRing coarsens against;
//            fitsSteps the same as a step generator (a step per box: ring.js's sliced re-plan).
// commit / fallback / free run raw GL: inside a gen run (the scheduler's
// renderer.resetState restores three's state after it), else the caller resets.
//
// The draw (terrain.js): `uniforms` (shared objects) + FAR_DECL_GLSL; the
// sector table (uFarSec, RGBA32F, one column per look.sectors index):
//   row 0  the inner rect in atlas uv [a0, b0, a1, b1]
//   row 1  rgb (fallback, linear) + state (0 none, 1 atlas, 2 flat)
//   row 2  the classes rect [x0, z0, 1 / w, 1 / h] (game m)
//   row 3  look's use rect, row 4 its own rect (the pick)
// and the chunk lists (uFarChunk, R32F, look.table(): K a chunk, -1 padded).
// farTap(p, dx, dy) = look.at's sector (the deepest own square among the
// chunk list's use rects holding p), its uv held one resident texel inside
// (ring.js ringRectClamp), sampled with p's gradients (textureGrad: the
// derivatives come from the caller, taken before any branch); a sector not
// resident (or no sector: beyond the ring) is the pool's no-slot grey 0.5
// (gmtex.js gmVtTap). farPick is its JS twin (scripts/check-groundm.mjs).
import * as THREE from 'three';
import { ringLook } from './ringlook.js';

export const FAR_PAD = 16, FAR_LEVELS = 5, FAR_CELL = 16;
const al = (v) => Math.ceil(v / FAR_CELL) * FAR_CELL;

// the atlas size of a device (renderconfig vt.far): the tier's, lowered by
// its class, clamped to the GL limit -> [w, h] (multiples of FAR_CELL) | null
export function farAtlasPx(far, tier, cls, maxTex = Infinity) {
  if (!far || !far.px) return null;
  const t = far.px[tier] || far.px[Object.keys(far.px).map(Number).sort((a, b) => a - b)[0]];
  const c = (far.by_class && far.by_class[cls]) || t;
  const lim = Math.floor(maxTex / FAR_CELL) * FAR_CELL;
  return [Math.min(t[0], c[0], lim), Math.min(t[1], c[1], lim)];
}

// the box an entry takes (texels, incl. the pad)
export function farBox(px, level) {
  const iw = Math.max(1, px[0] >> level), ih = Math.max(1, px[1] >> level);
  return { iw, ih, w: al(iw + 2 * FAR_PAD), h: al(ih + 2 * FAR_PAD) };
}

// first-fit over a FAR_CELL grid (bottom-left): place(bw, bh) cells -> { x, y } | null.
// run[j][i] (free cells upward from row j) depends on column i alone, so a take / give
// marks its columns dirty and place() rebuilds only those (xr-stalls: the whole-grid
// rebuild per place was most of a dry-run pack — the same run values, the same placements)
export function farPacker(w, h) {
  const gw = w / FAR_CELL, gh = h / FAR_CELL, occ = new Uint8Array(gw * gh), run = new Uint16Array(gw * gh);
  let d0 = 0, d1 = gw;          // the dirty columns [d0, d1)
  const mark = (x, y, bw, bh, v) => {
    for (let j = y; j < y + bh; j++) occ.fill(v, j * gw + x, j * gw + x + bw);
    if (x < d0) d0 = x;
    if (x + bw > d1) d1 = x + bw;
  };
  return {
    place(bw, bh) {
      if (bw > gw || bh > gh) return null;
      // run[j][i]: free cells upward from row j (the dirty columns only)
      for (let j = gh - 1; j >= 0; j--) for (let i = d0; i < d1; i++) run[j * gw + i] = occ[j * gw + i] ? 0 : (j + 1 < gh ? run[(j + 1) * gw + i] : 0) + 1;
      d0 = gw; d1 = 0;
      for (let j = 0; j + bh <= gh; j++) {
        let n = 0;
        for (let i = 0; i < gw; i++) {
          n = run[j * gw + i] >= bh ? n + 1 : 0;
          if (n === bw) return { x: i - bw + 1, y: j };
        }
      }
      return null;
    },
    take(p, bw, bh) { mark(p.x, p.y, bw, bh, 1); },
    give(p, bw, bh) { mark(p.x, p.y, bw, bh, 0); },
  };
}

// the dry run: every [{ px, level }] packed into w x h (largest first). farFitsSteps
// yields after each placed box (the ring's re-plan runs it on the frame budget:
// ring.js applySteps); farFits = its drain, the same answer
export function* farFitsSteps(w, h, list) {
  const P = farPacker(w, h);
  const boxes = list.map((e) => farBox(e.px, e.level)).sort((a, b) => b.h - a.h || b.w - a.w);
  for (const b of boxes) {
    const p = P.place(b.w / FAR_CELL, b.h / FAR_CELL);
    if (!p) return false;
    P.take(p, b.w / FAR_CELL, b.h / FAR_CELL);
    yield;
  }
  return true;
}
export function farFits(w, h, list) {
  const it = farFitsSteps(w, h, list);
  for (;;) { const s = it.next(); if (s.done) return s.value; }
}

// the bytes of a w x h atlas with its levels (RGBA8)
export const farBytes = (w, h) => { let b = 0; for (let l = 0; l < FAR_LEVELS; l++) b += 4 * Math.max(1, w >> l) * Math.max(1, h >> l); return b; };

// the sector table + chunk lists (pure: no GL) — look = ringLook(classes)
export function farTables(look) {
  const N = Math.max(1, look.sectors.length), sec = new Float32Array(4 * N * 5);
  look.sectors.forEach((s, i) => {
    const r = s.rect;
    sec.set([r[0], r[1], 1 / (r[2] - r[0]), 1 / (r[3] - r[1])], 4 * (2 * N + i));
    sec.set(s.use, 4 * (3 * N + i));
    sec.set(s.own, 4 * (4 * N + i));
  });
  const t = look.table();
  return { N, sec, chunk: { ...t, data: Float32Array.from(t.data) } };
}

// JS twin of farTap's pick over the tables (sector index | -1)
export function farPick(T, look, x, z) {
  const c = T.chunk, cx = Math.floor(x / look.chunkM) - c.cx0, cz = Math.floor(z / look.chunkM) - c.cz0;
  if (cx < 0 || cz < 0 || cx >= c.nx || cz >= c.nz) return -1;
  const S = T.sec, N = T.N, mg = (row, i) => {
    const o = 4 * (row * N + i);
    return Math.min(Math.min(x - S[o], S[o + 2] - x), Math.min(z - S[o + 1], S[o + 3] - z));
  };
  let best = -1, bm = -1e30;
  for (let k = 0; k < c.K; k++) {
    const i = c.data[(cz * c.nx + cx) * c.K + k];
    if (i < 0) break;
    if (mg(3, i) < 0) continue;
    const m = mg(4, i);
    if (m > bm) { bm = m; best = i; }
  }
  return best;
}

export const farDecl = (K) => `
uniform sampler2D uFarAtlas;
uniform highp sampler2D uFarSec;
uniform highp sampler2D uFarChunk;
uniform vec4 uFarBox;
uniform vec3 uFarDim;
float farMg(vec4 r, vec2 p) { return min(min(p.x - r.x, r.z - p.x), min(p.y - r.y, r.w - p.y)); }
vec3 farTap(vec2 p, vec2 dx, vec2 dy) {
  ivec2 c = ivec2(floor(p / uFarDim.x)) - ivec2(uFarBox.xy);
  if (c.x < 0 || c.y < 0 || c.x >= int(uFarBox.z) || c.y >= int(uFarBox.w)) return vec3(0.5);
  int best = -1;
  float bm = -1e30;
  for (int k = 0; k < ${K}; k++) {
    int i = int(texelFetch(uFarChunk, ivec2(c.x * ${K} + k, c.y), 0).r);
    if (i < 0) break;
    if (farMg(texelFetch(uFarSec, ivec2(i, 3), 0), p) < 0.0) continue;
    float m = farMg(texelFetch(uFarSec, ivec2(i, 4), 0), p);
    if (m > bm) { bm = m; best = i; }
  }
  if (best < 0) return vec3(0.5);
  vec4 s = texelFetch(uFarSec, ivec2(best, 1), 0);
  if (s.a < 0.5) return vec3(0.5);
  if (s.a > 1.5) return s.rgb;
  vec4 a = texelFetch(uFarSec, ivec2(best, 0), 0), r = texelFetch(uFarSec, ivec2(best, 2), 0);
  vec2 sz = a.zw - a.xy, g = sz * vec2(r.z, -r.w);
  vec2 uv = a.xy + vec2((p.x - r.x) * r.z, 1.0 - (p.y - r.y) * r.w) * sz;
  return textureGrad(uFarAtlas, clamp(uv, a.xy + uFarDim.yz, a.zw - uFarDim.yz), dx * g, dy * g).rgb;
}
`;

export function createFarAtlas(getRenderer, { w, h, classes }) {
  const look = ringLook(classes), T = farTables(look), N = T.N;
  const idOf = new Map(look.sectors.map((s, i) => [s.id, i]));
  const secTex = new THREE.DataTexture(T.sec, N, 5, THREE.RGBAFormat, THREE.FloatType);
  const c = T.chunk, chunkTex = new THREE.DataTexture(c.data, Math.max(1, c.nx * c.K), Math.max(1, c.nz), THREE.RedFormat, THREE.FloatType);
  for (const t of [secTex, chunkTex]) { t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.needsUpdate = true; }
  const tex = new THREE.DataTexture(null, w, h);
  tex.format = THREE.RGBAFormat; tex.type = THREE.UnsignedByteType; tex.colorSpace = THREE.SRGBColorSpace; tex.internalFormat = 'SRGB8_ALPHA8';
  tex.minFilter = THREE.LinearMipmapLinearFilter; tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping; tex.generateMipmaps = false;
  tex.mipmaps = Array.from({ length: FAR_LEVELS }, (_, l) => ({ data: null, width: Math.max(1, w >> l), height: Math.max(1, h >> l) }));
  tex.source.dataReady = false;
  const uniforms = {
    uFarAtlas: { value: tex }, uFarSec: { value: secTex }, uFarChunk: { value: chunkTex },
    uFarBox: { value: new THREE.Vector4(c.cx0, c.cz0, c.nx, c.nz) },
    uFarDim: { value: new THREE.Vector3(look.chunkM, 1 / w, 1 / h) },
  };
  // res: id -> the resident entry; qk: id -> the mean sRGB8 of a resident QUICK entry
  const P = farPacker(w, h), res = new Map(), pend = new Map(), flat = new Map(), qk = new Map(), subs = [];
  let G = null;
  const A = {
    look, uniforms, K: c.K, version: 0,
    budget: { w, h, levels: FAR_LEVELS, pad: FAR_PAD, bytes: farBytes(w, h), fits: (list) => farFits(w, h, list),
      fitsSteps: (list) => farFitsSteps(w, h, list) },
    onChange(fn) { subs.push(fn); },
    // the sectors meeting [x0, z0, x1, z1] all resident or flat (the far node's colour, groundtree.js)
    covers(x0, z0, x1, z1) {
      for (const s of look.sectors) {
        const u = s.use;
        if (u[0] < x1 && u[2] > x0 && u[1] < z1 && u[3] > z0 && !res.has(s.id) && !flat.has(s.id)) return false;
      }
      return true;
    },
    alloc(id, px, level, quick = false) {
      if (!idOf.has(id)) throw new Error(`far atlas: no sector ${id}`);
      if (pend.has(id)) A.free(id, true);
      const b = farBox(px, level);
      let p = P.place(b.w / FAR_CELL, b.h / FAR_CELL);
      // short of room: the quick entries go flat (other sectors' first), one at a time until it fits
      if (!p && !quick) {
        for (const q of [...qk.keys()].sort((a, c) => (a === id) - (c === id))) {
          A.fallback(q, qk.get(q));
          if ((p = P.place(b.w / FAR_CELL, b.h / FAR_CELL))) break;
        }
      }
      if (!p) return null;
      P.take(p, b.w / FAR_CELL, b.h / FAR_CELL);
      const x = p.x * FAR_CELL, y = p.y * FAR_CELL;
      const e = { id, level, x, y, w: b.w, h: b.h, inner: [x + FAR_PAD, y + FAR_PAD, x + FAR_PAD + b.iw, y + FAR_PAD + b.ih], px: [b.iw, b.ih] };
      pend.set(id, e);
      gl0();
      return e;
    },
    target(e) { const g = gl0(); return { framebuffer: g.f0, viewport: [e.inner[0], e.inner[1], e.px[0], e.px[1]] }; },
    upload(e, rgba) {
      if (pend.get(e.id) !== e) throw new Error(`far atlas: ${e.id} is not the pending entry`);
      const g = gl0(), gl = g.gl;
      gl.bindTexture(gl.TEXTURE_2D, g.t);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, e.inner[0], e.inner[1], e.px[0], e.px[1], gl.RGBA, gl.UNSIGNED_BYTE, rgba);
    },
    commit(e, rgb = null) {
      if (pend.get(e.id) !== e) throw new Error(`far atlas: ${e.id} is not the pending entry`);
      fill(gl0(), e);
      pend.delete(e.id);
      drop(e.id);
      res.set(e.id, e);
      if (rgb) qk.set(e.id, [rgb[0], rgb[1], rgb[2]]);
      row(e.id);
      bump([e.id]);
    },
    fallback(id, rgb) {
      drop(id);
      const col = new THREE.Color().setRGB(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255, THREE.SRGBColorSpace);
      flat.set(id, [col.r, col.g, col.b]);
      row(id);
      bump([id]);
    },
    free(id, quiet = false) {
      const e = pend.get(id);
      if (e) { P.give({ x: e.x / FAR_CELL, y: e.y / FAR_CELL }, e.w / FAR_CELL, e.h / FAR_CELL); pend.delete(id); }
      if (quiet) return;
      if (res.has(id) || flat.has(id)) { drop(id); row(id); bump([id]); }
    },
    lookup(id) { return res.has(id) ? { entry: res.get(id), quick: qk.has(id) } : flat.has(id) ? { rgb: flat.get(id) } : null; },
    dispose() {
      if (G) { const gl = G.gl; for (const f of [G.f0, G.fr, G.fd]) gl.deleteFramebuffer(f); for (const t of [G.sr, G.sc]) t.dispose(); }
      for (const t of [tex, secTex, chunkTex]) t.dispose();
      G = null;
    },
  };
  function drop(id) {
    const e = res.get(id);
    if (e) { P.give({ x: e.x / FAR_CELL, y: e.y / FAR_CELL }, e.w / FAR_CELL, e.h / FAR_CELL); res.delete(id); }
    flat.delete(id);
    qk.delete(id);
  }
  function row(id) {
    const i = idOf.get(id), e = res.get(id), f = flat.get(id), S = T.sec;
    S.set(e ? [e.inner[0] / w, e.inner[1] / h, e.inner[2] / w, e.inner[3] / h] : [0, 0, 0, 0], 4 * i);
    S.set(e ? [0, 0, 0, 1] : f ? [f[0], f[1], f[2], 2] : [0, 0, 0, 0], 4 * (N + i));
    secTex.needsUpdate = true;
  }
  function bump(ids) { A.version++; for (const fn of subs) fn(ids); }
  // the GL objects (lazy): the atlas storage, its level-0 FBO, the blit
  // pair and the two pad strips (a row strip w x 2, a column strip 2 x h)
  function gl0() {
    if (G) return G;
    const R = getRenderer();
    if (!R) throw new Error('far atlas: no renderer');
    const gl = R.getContext();
    tex.needsUpdate = true;
    R.initTexture(tex);
    const strip = (sw, sh) => {
      const t = new THREE.DataTexture(null, sw, sh);
      t.format = THREE.RGBAFormat; t.type = THREE.UnsignedByteType; t.colorSpace = THREE.SRGBColorSpace; t.internalFormat = 'SRGB8_ALPHA8';
      t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false;
      t.mipmaps = [{ data: null, width: sw, height: sh }]; t.source.dataReady = false; t.needsUpdate = true;
      R.initTexture(t);
      return t;
    };
    G = { gl, R, f0: gl.createFramebuffer(), fr: gl.createFramebuffer(), fd: gl.createFramebuffer(), sr: strip(w, 2), sc: strip(2, h) };
    G.t = R.properties.get(tex).__webglTexture;
    gl.bindFramebuffer(gl.FRAMEBUFFER, G.f0);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, G.t, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return G;
  }
  // the pad (edge replicated) + levels 1.. of an entry's box
  function fill(g, e) {
    const gl = g.gl, [x0, y0, x1, y1] = e.inner, bx0 = e.x, by0 = e.y, bx1 = e.x + e.w, by1 = e.y + e.h;
    const sr = g.R.properties.get(g.sr).__webglTexture, sc = g.R.properties.get(g.sc).__webglTexture;
    const blit = (src, sl, dst, dl, s, d, f) => {
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, g.fr);
      gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, src, sl);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, g.fd);
      gl.framebufferTexture2D(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, dst, dl);
      gl.blitFramebuffer(s[0], s[1], s[2], s[3], d[0], d[1], d[2], d[3], gl.COLOR_BUFFER_BIT, f);
    };
    const N_ = gl.NEAREST;
    gl.disable(gl.SCISSOR_TEST);
    // the inner's edge rows / columns -> the strips
    blit(g.t, 0, sr, 0, [x0, y0, x1, y0 + 1], [x0, 0, x1, 1], N_);
    blit(g.t, 0, sr, 0, [x0, y1 - 1, x1, y1], [x0, 1, x1, 2], N_);
    blit(g.t, 0, sc, 0, [x0, y0, x0 + 1, y1], [0, y0, 1, y1], N_);
    blit(g.t, 0, sc, 0, [x1 - 1, y0, x1, y1], [1, y0, 2, y1], N_);
    // ... stretched over the pad: sides, then the corners (a strip's end texel)
    blit(sr, 0, g.t, 0, [x0, 0, x1, 1], [x0, by0, x1, y0], N_);
    blit(sr, 0, g.t, 0, [x0, 1, x1, 2], [x0, y1, x1, by1], N_);
    blit(sc, 0, g.t, 0, [0, y0, 1, y1], [bx0, y0, x0, y1], N_);
    blit(sc, 0, g.t, 0, [1, y0, 2, y1], [x1, y0, bx1, y1], N_);
    blit(sr, 0, g.t, 0, [x0, 0, x0 + 1, 1], [bx0, by0, x0, y0], N_);
    blit(sr, 0, g.t, 0, [x1 - 1, 0, x1, 1], [x1, by0, bx1, y0], N_);
    blit(sr, 0, g.t, 0, [x0, 1, x0 + 1, 2], [bx0, y1, x0, by1], N_);
    blit(sr, 0, g.t, 0, [x1 - 1, 1, x1, 2], [x1, y1, bx1, by1], N_);
    // the levels: 2:1 LINEAR (the box filter; sRGB-aware), the box only
    for (let l = 0; l + 1 < FAR_LEVELS; l++) {
      blit(g.t, l, g.t, l + 1, [bx0 >> l, by0 >> l, bx1 >> l, by1 >> l], [bx0 >> (l + 1), by0 >> (l + 1), bx1 >> (l + 1), by1 >> (l + 1)], gl.LINEAR);
    }
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
  }
  return A;
}
